import { useEffect, useRef, useState, useCallback } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Loader2,
  Scissors,
} from "lucide-react";

/**
 * Custom ASN YouTube Player
 * Wraps YouTube's iframe API with custom controls and ASN branding
 * Hides all YouTube UI elements including view count
 */
export const YouTubePlayer = ({
  youtubeUrl,
  isLive = false,
  autoPlay = true,
  onReady,
  onClipCreate, // Callback when clip button is clicked, receives current time
  showClipButton = false, // Whether to show the clip creation button
}) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const [volume, setVolume] = useState(100);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);

  // Extract YouTube video ID from various URL formats
  const extractVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/, // Direct video ID
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const videoId = extractVideoId(youtubeUrl);

  // Load YouTube IFrame API
  useEffect(() => {
    if (!videoId) return;

    // Load the IFrame Player API code asynchronously
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Create YouTube player when API is ready
    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        // Destroy existing player if any
        if (playerRef.current) {
          playerRef.current.destroy();
        }

        playerRef.current = new window.YT.Player(iframeRef.current, {
          videoId: videoId,
          host: "https://www.youtube-nocookie.com", // Privacy-enhanced mode
          playerVars: {
            autoplay: autoPlay ? 1 : 0,
            mute: 1, // Start muted for autoplay to work
            controls: 0, // Hide YouTube controls
            showinfo: 0, // Hide video info
            rel: 0, // No related videos
            modestbranding: 1, // Minimal YouTube branding
            playsinline: 1, // Inline playback on mobile
            iv_load_policy: 3, // Hide annotations
            fs: 0, // Disable YouTube fullscreen (we use our own)
            disablekb: 1, // Disable keyboard controls (we handle them)
            origin: window.location.origin,
            enablejsapi: 1,
            cc_load_policy: 0, // Hide closed captions by default
            end: 0, // Don't show end screen
          },
          events: {
            onReady: (event) => {
              setPlayerReady(true);
              setIsLoading(false);
              setDuration(event.target.getDuration());
              if (autoPlay) {
                event.target.playVideo();
                setIsPlaying(true);
              }
              onReady?.();
            },
            onStateChange: (event) => {
              switch (event.data) {
                case window.YT.PlayerState.PLAYING:
                  setIsPlaying(true);
                  setIsLoading(false);
                  break;
                case window.YT.PlayerState.PAUSED:
                  setIsPlaying(false);
                  break;
                case window.YT.PlayerState.BUFFERING:
                  setIsLoading(true);
                  break;
                case window.YT.PlayerState.ENDED:
                  setIsPlaying(false);
                  break;
                default:
                  break;
              }
            },
            onError: (event) => {
              console.error("YouTube player error:", event.data);
              setIsLoading(false);
            },
          },
        });
      }
    };

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoId, autoPlay, onReady]);

  // Update current time periodically
  useEffect(() => {
    if (!playerReady) return;

    const interval = setInterval(() => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === "function") {
        setCurrentTime(playerRef.current.getCurrentTime());
        setDuration(playerRef.current.getDuration());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [playerReady]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (isPlaying) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleMouseMove = () => resetControlsTimeout();
  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Controls
  const togglePlay = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (isMuted) {
      playerRef.current.unMute();
      playerRef.current.setVolume(volume);
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      if (newVolume === 0) {
        playerRef.current.mute();
        setIsMuted(true);
      } else if (isMuted) {
        playerRef.current.unMute();
        setIsMuted(false);
      }
    }
  };

  const handleSeek = (e) => {
    if (!playerRef.current || isLive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    playerRef.current.seekTo(seekTime, true);
    setCurrentTime(seekTime);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!videoId) {
    return (
      <div className="relative w-full aspect-video bg-black flex items-center justify-center">
        <p className="text-zinc-500">No video URL provided</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black group"
      style={{ overflow: 'hidden' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="youtube-player"
    >
      {/* YouTube Iframe Container - scaled up to clip edges where YouTube shows UI */}
      <div 
        className="absolute overflow-hidden"
        style={{ 
          top: '-5%', 
          left: '-2%', 
          width: '104%', 
          height: '115%',
        }}
      >
        <div
          ref={iframeRef}
          className="w-full h-full"
        />
      </div>

      {/* Clickable overlay to handle play/pause */}
      <div
        className="absolute inset-0 z-10"
        onClick={togglePlay}
        style={{ pointerEvents: playerReady ? "auto" : "none" }}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-25">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Live Badge */}
      {isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest z-30">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
      )}

      {/* Custom Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 z-20 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Center Play Button */}
        <button
          className="absolute inset-0 flex items-center justify-center"
          onClick={togglePlay}
        >
          {!isPlaying && !isLoading && (
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
          )}
        </button>

        {/* Bottom Controls Bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Progress Bar (hidden for live) */}
          {!isLive && duration > 0 && (
            <div
              className="relative h-1 bg-white/20 mb-4 cursor-pointer group/progress"
              onClick={(e) => {
                e.stopPropagation();
                handleSeek(e);
              }}
            >
              <div
                className="absolute h-full bg-red-600"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-red-600 rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white" fill="white" />
                )}
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleVolumeChange(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-0 group-hover/volume:w-20 transition-all duration-200 accent-red-600"
                />
              </div>

              {/* Time */}
              {!isLive && (
                <span className="text-white text-sm font-mono">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              )}

              {/* Live indicator text */}
              {isLive && (
                <span className="text-red-500 text-sm font-bold uppercase">
                  Live Broadcast
                </span>
              )}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Clip Creation Button - only for non-live content */}
              {!isLive && showClipButton && onClipCreate && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClipCreate(currentTime);
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
                  title="Create Clip"
                  data-testid="clip-create-btn"
                >
                  <Scissors className="w-5 h-5 text-white group-hover:text-orange-500 transition-colors" />
                </button>
              )}
              
              {/* Fullscreen */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5 text-white" />
                ) : (
                  <Maximize className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;
